import React, { useContext, useEffect } from 'react';
import {
    Box,
    Button,
    ColumnLayout,
    Container,
    Flashbar,
    Grid,
    Header,
    Icon,
    Link,
    SpaceBetween,
    StatusIndicator,
} from '@amzn/awsui-components-console';

import '../styles/landing-page.scss';
import deviceFarmHighLevelArch from '../images/deviceFarmHighLevelArch.png';
import { generateJwtToken } from './manual-testing/apis';
import { generateManualTestingUrl } from './lib/utils';
import * as Constants from './lib/constants';
import { RegionContext } from './CustomContext';

export default function DeviceFarmHome() {
    /*
    // set up content and add flashbar into component as needed
    const [release, setRelease] = React.useState([
        {
            type: 'info',
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setRelease([]),
            content: null
        },
    ]);
    */
    const [errorStatus, setErrorStatus] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [tickets, setTickets] = React.useState([]);
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);
    const onSubmit = () => {
        (async function () {
            setLoading(true);
            try {
                const response = await generateJwtToken();
                const token = response['response'];
                const url = generateManualTestingUrl(token, selectedRegion.value);
                window.location.href = url;
            } catch (e) {
                console.error(e);
                setErrorStatus(true);
            }
            setLoading(false);
        })();
    };

    const generateWhatsComingBox = (headerText, paragraphText) => (
        <div>
            <Box variant="strong" padding={{ top: 'n' }}>
                {headerText}
            </Box>
            <Box variant="p">{paragraphText}</Box>
        </div>
    );
    const generateLink = (link, text) => (
        <li>
            <Link href={link} external>
                {text}
            </Link>
        </li>
    );

    return (
        <div>
            <Box padding="s" margin={{ bottom: 'l' }}>
                <Grid className="custom-home__header" gridDefinition={[{ colspan: { xxs: 12 } }]}>
                    <Box padding={{ vertical: 'xxl' }}>
                        <Grid
                            gridDefinition={[
                                { colspan: { xxs: 10, s: 6, l: 5, xl: 6 }, offset: { xxs: 1, l: 2 } },
                                { colspan: { xxs: 10, s: 4, l: 3, xl: 2 }, offset: { xxs: 1, s: 0 } },
                            ]}
                        >
                            <div className="custom-home__header-title">
                                <Box
                                    variant="h1"
                                    textAlign="left"
                                    fontWeight="bold"
                                    padding="n"
                                    fontSize="display-l"
                                    color="inherit"
                                >
                                    Amazon Device Farm
                                </Box>
                                <Box
                                    className="custom-home__header-sub-title"
                                    variant="small"
                                    textAlign="left"
                                    fontWeight="light"
                                    padding={{ bottom: 'xs' }}
                                    fontSize="body-m"
                                >
                                    Formerly DS2 Device Farm
                                </Box>
                                <Box
                                    varient="h2"
                                    textAlign="left"
                                    fontWeight="light"
                                    padding={{ bottom: 's' }}
                                    fontSize="display-l"
                                    color="inherit"
                                >
                                    Access to a myriad of devices is only a click away...
                                </Box>
                                <Box variant="p" textAlign="left" fontWeight="light" fontSize="body-m" color="inherit">
                                    Amazon Device Farm empowers customers to access FOS devices and Android phones in real
                                    time. Simply check out devices from your web browser and perform any user
                                    interaction on the device. You can play a video on Fire TV, navigate to Settings in
                                    a Tablet, open apps, install builds, sideload apks, take screenshots, view logs, and
                                    much more. The Device Farm lab can be accessed from anywhere in the world using
                                    midway authentication. You can also see the device metadata and metrics from this
                                    dashboard.
                                </Box>
                            </div>
                            <div>
                                <Container>
                                    <SpaceBetween size="xl">
                                        <Box variant="h2" padding="n">
                                            Device checkout
                                        </Box>
                                        <Box variant="p">
                                            Choose a device and perform any desired actions from your web browser. Click{' '}
                                            <Link href={Constants.TUTORIAL_URL} external>
                                                Help
                                            </Link>{' '}
                                            to watch the tutorial videos to learn more or click Get started to find your
                                            device.
                                        </Box>
                                        <Button
                                            loading={loading}
                                            variant="primary"
                                            onClick={onSubmit}
                                            id="get-started-btn"
                                        >
                                            Get started
                                        </Button>

                                        {errorStatus ? (
                                            <div>
                                                <StatusIndicator type="error">
                                                    [Error] Cannot generate a jwt token for you, please try it again.
                                                </StatusIndicator>
                                            </div>
                                        ) : null}
                                    </SpaceBetween>
                                </Container>
                            </div>
                        </Grid>
                    </Box>
                </Grid>

                <Box padding={{ top: 'xxl' }}>
                    <Grid
                        gridDefinition={[
                            { colspan: { xxs: 10, s: 6, l: 5, xl: 6 }, offset: { xxs: 1, l: 2 } },
                            { colspan: { xxs: 10, s: 4, l: 3, xl: 2 }, offset: { xxs: 1, s: 0 } },
                        ]}
                    >
                        <div>
                            <SpaceBetween size="l">
                                <div>
                                    <Container header={<Header variant="h2">How it works</Header>}>
                                        <ColumnLayout columns={1} variant="text-grid">
                                            <Box variant="p">
                                                Device Farm is a unique lab solution built on Native AWS (NAWS). AWS
                                                Internet of Things (IoT) Services are used to connect the devices, which
                                                are tethered to physical hosts to the cloud. AWS IoT GreenGrass is a
                                                client-side software that runs on ubuntu desktops. GreenGrass
                                                communicates with the cloud using IOT Core, which runs on the cloud
                                                side. This enables two-way communication between the cloud and the
                                                client desktops, which in turn enables the user to interact with the
                                                devices. To support various use cases for our customers, micro services
                                                are built in lambda and use Dynamo DB as the database. These lambda
                                                functions contain the business logic and connect with IoT Core for
                                                device interactions. All the APIs are fronted by an API gateway that can
                                                be accessed from the Device Farm dashboard or from other systems via the
                                                right authentication and authorization mechanism. Cognito is used for
                                                the front-end authentication, which supports both Amazon login using
                                                midway and external customer login with creation of a new account. Click{' '}
                                                <Link href="https://w.amazon.com/bin/view/DS2DeviceFarm/Technical_Details/Design/">
                                                    here
                                                </Link>{' '}
                                                to access the detailed high-level design specifications and{' '}
                                                <Link href="https://w.amazon.com/bin/view/DS2DeviceFarm/Technical_Details/Design/LowLevelDesign/AuthNandZ/">
                                                    here
                                                </Link>{' '}
                                                for the low level component designs.
                                            </Box>
                                            <img
                                                className="custom-home-image__df-high-arch"
                                                src={deviceFarmHighLevelArch}
                                                alt="Amazon Device Farm High Level Architecture Diagram"
                                                fetchpriority="high"
                                                loading="eager"
                                            />
                                        </ColumnLayout>
                                    </Container>
                                </div>
                                <div>
                                    <Container header={<Header variant="h2">Coming soon</Header>}>
                                        <ColumnLayout columns={2} variant="text-grid">
                                            {generateWhatsComingBox(
                                                'Pool management',
                                                'Self-service support for creating private pool with devices dedicated for a group to perform manual or automation testing.'
                                            )}
                                            {generateWhatsComingBox(
                                                'Device configuration',
                                                'Configure devices or pools with predefined conditions like software build, WIFI connectivity, endpoint etc.'
                                            )}
                                        </ColumnLayout>
                                    </Container>
                                </div>
                            </SpaceBetween>
                        </div>

                        <div>
                            <SpaceBetween size="xxl">
                                <Container
                                    header={
                                        <Header variant="h2">
                                            Additional information <Icon name="external" />
                                        </Header>
                                    }
                                >
                                    <ul className="custom-list-separator">
                                        {generateLink('https://docs.loom.devices.amazon.dev/device-farm/', 'Overview')}
                                        {generateLink(Constants.TUTORIAL_URL, 'Watch tutorials')}
                                        {generateLink(
                                            'https://taskei.amazon.dev/rooms/37bc9034-fa07-413c-9dbf-396b19d2e644/tasks?ss=3667b1b9-5554-4f76-8b80-82a12aee6d8f',
                                            'View 2026 goal'
                                        )}
                                        {generateLink(
                                            'https://taskei.amazon.dev/rooms/37bc9034-fa07-413c-9dbf-396b19d2e644/tasks?ss=a522aee8-2168-4d74-bae7-8f0b778ca8ab',
                                            'Upvote feature requests'
                                        )}
                                        {generateLink(Constants.CONTACT_US, 'Contact us')}
                                        {generateLink(
                                            'https://t.corp.amazon.com/create/templates/7b41de12-761a-40e2-98b6-c302121b351a',
                                            'Report bug'
                                        )}
                                    </ul>
                                </Container>
                            </SpaceBetween>
                        </div>
                    </Grid>
                </Box>
            </Box>
        </div>
    );
}
